import { useEffect, useState } from 'react';
import { useApi } from '../lib/api';

interface Template {
  id: string;
  name: string;
  description: string | null;
  content: string;
  isShared: boolean;
  isOwner: boolean;
  usageCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  owner?: { name: string; email: string } | null;
}

export function Templates() {
  const { get, post, patch, delete: del } = useApi();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  async function fetchTemplates() {
    try {
      setLoading(true);
      const data = await get<{ templates: Template[] }>('/templates');
      setTemplates(data.templates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(data: { name: string; content: string; description?: string; isShared?: boolean }) {
    try {
      await post('/templates', data);
      await fetchTemplates();
      setShowCreateModal(false);
    } catch (err) {
      alert('Failed to create template');
    }
  }

  async function handleUpdate(id: string, data: { name?: string; content?: string; description?: string; isShared?: boolean }) {
    try {
      await patch(`/templates/${id}`, data);
      await fetchTemplates();
      setEditingTemplate(null);
    } catch (err) {
      alert('Failed to update template');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await del(`/templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert('Failed to delete template');
    }
  }

  async function handleUseTemplate(template: Template) {
    // Copy content to clipboard and record usage
    await navigator.clipboard.writeText(template.content);
    try {
      await post(`/templates/${template.id}/use`);
      await fetchTemplates();
    } catch (err) {
      // Non-critical, ignore
    }
    alert('Template content copied to clipboard!');
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Agenda Templates</h1>
          <p className="mt-1 text-slate-400">
            Save and reuse your demo agendas
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
        >
          New Template
        </button>
      </div>

      {/* Templates grid */}
      {templates.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => setEditingTemplate(template)}
              onDelete={() => handleDelete(template.id)}
              onUse={() => handleUseTemplate(template)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-slate-800 rounded-lg p-8 text-center border border-slate-700">
          <p className="text-slate-400">No templates yet</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
          >
            Create your first template
          </button>
        </div>
      )}

      {/* Create modal */}
      {showCreateModal && (
        <TemplateModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
        />
      )}

      {/* Edit modal */}
      {editingTemplate && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={(data) => handleUpdate(editingTemplate.id, data)}
        />
      )}
    </div>
  );
}

function TemplateCard({
  template,
  onEdit,
  onDelete,
  onUse,
}: {
  template: Template;
  onEdit: () => void;
  onDelete: () => void;
  onUse: () => void;
}) {
  const itemCount = template.content.split('\n').filter((line) => line.trim()).length;

  return (
    <div className="bg-slate-800 rounded-lg p-4 border border-slate-700 hover:border-slate-600 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-medium truncate">{template.name}</h3>
          {template.description && (
            <p className="text-sm text-slate-400 mt-1 line-clamp-2">{template.description}</p>
          )}
        </div>
        {template.isShared && (
          <span className="ml-2 px-2 py-0.5 bg-blue-900/50 text-blue-400 text-xs rounded-full">
            Shared
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center text-sm text-slate-500 space-x-4">
        <span>{itemCount} items</span>
        <span>Used {template.usageCount}x</span>
      </div>

      {!template.isOwner && template.owner && (
        <p className="mt-2 text-xs text-slate-500">
          By {template.owner.name || template.owner.email}
        </p>
      )}

      <div className="mt-4 flex items-center space-x-2">
        <button
          onClick={onUse}
          className="flex-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
        >
          Use
        </button>
        {template.isOwner && (
          <>
            <button
              onClick={onEdit}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-md"
            >
              Edit
            </button>
            <button
              onClick={onDelete}
              className="px-3 py-1.5 text-slate-400 hover:text-red-400"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function TemplateModal({
  template,
  onClose,
  onSave,
}: {
  template?: Template;
  onClose: () => void;
  onSave: (data: { name: string; content: string; description?: string; isShared?: boolean }) => void;
}) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [content, setContent] = useState(template?.content || '');
  const [isShared, setIsShared] = useState(template?.isShared || false);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !content.trim()) return;

    setSaving(true);
    try {
      await onSave({ name, content, description: description || undefined, isShared });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg p-6 w-full max-w-lg mx-4 border border-slate-700">
        <h2 className="text-lg font-medium text-white mb-4">
          {template ? 'Edit Template' : 'New Template'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., Product Demo Standard"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Brief description of when to use this template"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Agenda Items (one per line)
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 h-40 font-mono text-sm"
              placeholder="Introduction&#10;Key features&#10;Demo walkthrough&#10;Pricing&#10;Q&A"
              required
            />
          </div>

          <label className="flex items-center space-x-2 text-slate-300">
            <input
              type="checkbox"
              checked={isShared}
              onChange={(e) => setIsShared(e.target.checked)}
              className="rounded border-slate-700 bg-slate-900 text-green-500 focus:ring-green-500"
            />
            <span className="text-sm">Share with my team</span>
          </label>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving...' : template ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
